import type { CrateSpec, EnemySpec, GatePairSpec, HazardSpec, ObstacleSpec } from './level';

export type EncounterFamily = 'rescue' | 'barrel' | 'supply' | 'shortcut' | 'recovery';
export type EncounterPhase = 'introduction' | 'complication' | 'payoff' | 'recovery' | 'boss-preparation';
export type EncounterContent = {
  gates: GatePairSpec[]; crates: CrateSpec[]; enemies: EnemySpec[];
  hazards: HazardSpec[]; obstacles: ObstacleSpec[];
};
export type EncounterVariant = {
  id: string; family: EncounterFamily; phases: EncounterPhase[];
  safeLane: -1 | 1; reward: string; entryLane: -1 | 1; exitLane: -1 | 1;
  pressure: [number, number]; content: EncounterContent;
};
const pair = (z: number, value: number): GatePairSpec => ({
  z, left: { kind: 'num', value }, right: { kind: 'div', value: 2 },
});
const content = (parts: Partial<EncounterContent>): EncounterContent => ({
  gates: [], crates: [], enemies: [], hazards: [], obstacles: [], ...parts,
});
const enemy = (z: number): EnemySpec => ({ z, count: 15, x: -0.65, halfWidth: 0.3, killInterval: 0 });
const barrel = (z: number): ObstacleSpec => ({ z, x: -0.65, halfWidth: 0.15, hp: 16, kind: 'barrel' });
const wall = (z: number): ObstacleSpec => ({ z, x: -0.65, halfWidth: 0.25, hp: 12, kind: 'barricade' });
const spike = (z: number): HazardSpec => ({ z, x: -0.6, halfWidth: 0.3, penalty: 10 });
const crate = (z: number, x = -0.65): CrateSpec => ({ z, x, hp: 6 });
const active: EncounterPhase[] = ['complication', 'payoff'];
const recovery: EncounterPhase[] = ['recovery'];
const variant = (id: string, family: EncounterFamily, phases: EncounterPhase[], reward: string, parts: Partial<EncounterContent>, exitLane: -1 | 1 = -1): EncounterVariant => ({
  id, family, phases, safeLane: 1, reward, entryLane: -1, exitLane, pressure: [1, 2], content: content(parts),
});
// Positions are local to the phase. The opposite rail clears every threat at maximum width.
export const ENCOUNTERS: EncounterVariant[] = [
  variant('rescue-escort', 'rescue', active, 'reinforcements', { gates: [pair(5, 5), pair(35.5, 10)], enemies: [enemy(35)] }),
  variant('rescue-transfer', 'rescue', active, 'offset reinforcements', { gates: [pair(5, 5), pair(40.5, 10)], enemies: [enemy(40)] }),
  variant('barrel-single', 'barrel', active, 'reinforcements after suppression', { gates: [pair(5, 5), pair(75, 10)], enemies: [enemy(40)], obstacles: [barrel(35)] }),
  variant('barrel-relay', 'barrel', active, 'reinforcements after suppression', { gates: [pair(5, 5), pair(75, 10)], enemies: [enemy(43)], obstacles: [barrel(35), barrel(39)] }),
  variant('supply-straight', 'supply', active, 'weapon crate', { gates: [pair(5, 5), pair(75, 10)], crates: [crate(50)], obstacles: [wall(38)] }),
  variant('supply-delayed', 'supply', active, 'delayed weapon crate', { gates: [pair(5, 5), pair(75, 10)], crates: [crate(55, -0.4)], obstacles: [wall(40)] }),
  variant('shortcut-exit', 'shortcut', active, 'reinforcements', { gates: [pair(5, 10), pair(75, 5)], hazards: [spike(8)] }, 1),
  variant('shortcut-transfer', 'shortcut', active, 'reinforcements', { gates: [pair(5, 5), pair(75, 10)], hazards: [spike(72)] }),
  variant('recovery-recruits', 'recovery', recovery, 'reinforcements', { gates: [pair(20, 10), pair(50, 5)] }),
  variant('recovery-rate', 'recovery', recovery, 'rate or reinforcements', { gates: [pair(20, 10), { z: 50, left: { kind: 'rate', value: 1 }, right: { kind: 'num', value: 10 } }] }),
];

export function placeEncounter(variant: EncounterVariant, start: number, mirrored: boolean, pressure: number): EncounterContent {
  const sign = mirrored ? -1 : 1;
  const c = variant.content;
  return {
    gates: c.gates.map(g => ({ z: g.z + start, left: mirrored ? { ...g.right } : { ...g.left }, right: mirrored ? { ...g.left } : { ...g.right } })),
    crates: c.crates.map(o => ({ ...o, z: o.z + start, x: o.x * sign })),
    enemies: c.enemies.map(o => ({ ...o, z: o.z + start, x: o.x * sign, count: Math.round(o.count * Math.max(variant.pressure[0], Math.min(variant.pressure[1], pressure))) })),
    hazards: c.hazards.map(o => ({ ...o, z: o.z + start, x: o.x * sign, penalty: Math.round(o.penalty * Math.max(variant.pressure[0], Math.min(variant.pressure[1], pressure))) })),
    obstacles: c.obstacles.map(o => ({ ...o, z: o.z + start, x: o.x * sign })),
  };
}
